import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/da0r1je859p3u981e5qg";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐字段若被填则视为机器提交，不真正发送
    const honeypot = (fd.get("website_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("success");
      return;
    }

    setStatus("loading");
    setFormError("");
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(msg) || /form data is spam/i.test(responseText);

      if (!res.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(msg || "提交失败，请稍后再试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="mx-auto w-full max-w-7xl scroll-mt-20 px-4 py-16 md:px-6 md:py-24">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          <span className="text-sm font-medium tracking-wide text-secondary-600">联系我们</span>
          <h2 className="font-heading mt-2 text-2xl leading-snug text-foreground-950 md:text-4xl">
            有想法或建议？
            <br />
            欢迎随时聊聊
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是内容反馈、合作建议还是版权问题，都可以通过下方表单联系我们。我们会认真阅读每一条留言。
          </p>

          <div className="mt-8 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-600">
                <i className="ri-mail-line" />
              </span>
              <div>
                <div className="text-sm font-medium text-foreground-950">邮箱</div>
                <div className="text-sm text-foreground-600">hello@tongren-doujin.example</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary-100 text-xl text-secondary-600">
                <i className="ri-message-3-line" />
              </span>
              <div>
                <div className="text-sm font-medium text-foreground-950">反馈类型</div>
                <div className="text-sm text-foreground-600">内容反馈 / 合作建议 / 版权相关</div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
          <form
            data-readdy-form="同人动漫联系表单"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700">姓名</label>
                <input
                  name="name"
                  required
                  maxLength={30}
                  placeholder="怎么称呼你"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-foreground-700">邮箱</label>
                <input
                  name="email"
                  type="email"
                  required
                  maxLength={60}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm text-foreground-700">类型</label>
              <select
                name="type"
                className="w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
              >
                <option value="内容反馈">内容反馈</option>
                <option value="合作建议">合作建议</option>
                <option value="版权相关">版权相关</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm text-foreground-700">留言</label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请写下你想说的内容（不超过 500 字）"
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
              />
            </div>

            <div className="frm-hp">
              <label className="text-sm text-foreground-700">网站</label>
              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-1 inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin" />
                  提交中…
                </>
              ) : (
                <>
                  发送留言
                  <i className="ri-send-plane-line ml-2" />
                </>
              )}
            </button>

            {status === "success" && (
              <div className="rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                <i className="ri-checkbox-circle-line mr-2" />
                提交成功！感谢你的留言，我们会尽快回复。
              </div>
            )}
            {status === "error" && (
              <div className="rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-error-warning-line mr-2" />
                {formError}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}